import type { OptionalCategory } from './types';

/**
 * Known third-party trackers: how to recognise them (script / iframe URLs), what category they belong to,
 * which cookies they set. What each one is for, per language, lives in the language packs (`trackerPurposes`). Single source of truth for auto-blocking, the banner's categories,
 * cookie erasing on reject and the cookie tables.
 *
 * Durations are as documented by each provider (verify when the provider changes them).
 */

export type TrackerId =
  | 'ga4'
  | 'gtm'
  | 'googleAds'
  | 'metaPixel'
  | 'hotjar'
  | 'clarity'
  | 'linkedinInsight'
  | 'tiktokPixel'
  | 'youtube'
  | 'vimeo'
  | 'googleMaps';

export type DurationUnit = 'minute' | 'day' | 'month' | 'year';
/** `null` = session cookie. */
export type Duration = { value: number; unit: DurationUnit } | null;

export interface TrackerCookie {
  /** Exact name, or a pattern for names with a suffix (e.g. `_ga_<container id>`). */
  name: string | RegExp;
  /** Name as shown in tables, e.g. `_ga_*`. */
  display: string;
  duration: Duration;
  /** Set on the site's own domain (so it can be seen and erased) or by the provider's domain. */
  firstParty: boolean;
}

export interface Tracker {
  id: TrackerId;
  label: string;
  provider: string;
  category: OptionalCategory;
  /** Google tag: loaded and governed by Consent Mode in `googleMode: 'advanced'`, blocked in `'basic'`. */
  google?: boolean;
  /** Never blocked (a tag loader such as GTM decides per tag through Consent Mode). */
  neverBlock?: boolean;
  scripts: RegExp[];
  iframes: RegExp[];
  cookies: TrackerCookie[];
  onAccept?: () => void;
  onReject?: () => void;
}

type AnyFn = (...args: unknown[]) => void;
const call = (name: string, ...args: unknown[]): void => {
  const fn = (window as unknown as Record<string, unknown>)[name];
  if (typeof fn === 'function') (fn as AnyFn)(...args);
};

const years = (value: number): Duration => ({ value, unit: 'year' });
const months = (value: number): Duration => ({ value, unit: 'month' });
const days = (value: number): Duration => ({ value, unit: 'day' });
const minutes = (value: number): Duration => ({ value, unit: 'minute' });

export const TRACKERS: readonly Tracker[] = [
  {
    id: 'ga4',
    label: 'Google Analytics',
    provider: 'Google',
    category: 'analytics',
    google: true,
    scripts: [/googletagmanager\.com\/gtag\/js\?(?:.*&)?id=(?:G|GT|UA)-/i, /google-analytics\.com\/(?:analytics|ga)\.js/i],
    iframes: [],
    cookies: [
      { name: '_ga', display: '_ga', duration: years(2), firstParty: true },
      { name: /^_ga_/, display: '_ga_*', duration: years(2), firstParty: true },
      { name: '_gid', display: '_gid', duration: days(1), firstParty: true },
    ],
  },
  {
    id: 'gtm',
    label: 'Google Tag Manager',
    provider: 'Google',
    category: 'analytics',
    google: true,
    neverBlock: true,
    scripts: [/googletagmanager\.com\/gtm\.js/i],
    iframes: [/googletagmanager\.com\/ns\.html/i],
    cookies: [],
  },
  {
    id: 'googleAds',
    label: 'Google Ads',
    provider: 'Google',
    category: 'marketing',
    google: true,
    scripts: [
      /googletagmanager\.com\/gtag\/js\?(?:.*&)?id=(?:AW|DC)-/i,
      /googleadservices\.com\//i,
      /googlesyndication\.com\//i,
      /doubleclick\.net\//i,
    ],
    iframes: [/doubleclick\.net\//i],
    cookies: [{ name: /^_gcl_/, display: '_gcl_*', duration: months(3), firstParty: true }],
  },
  {
    id: 'metaPixel',
    label: 'Meta Pixel',
    provider: 'Meta',
    category: 'marketing',
    scripts: [/connect\.facebook\.net\/.*\/fbevents\.js/i, /connect\.facebook\.net\//i],
    iframes: [/facebook\.com\/tr/i],
    cookies: [
      { name: '_fbp', display: '_fbp', duration: months(3), firstParty: true },
      { name: '_fbc', display: '_fbc', duration: months(3), firstParty: true },
    ],
    onAccept: () => call('fbq', 'consent', 'grant'),
    onReject: () => call('fbq', 'consent', 'revoke'),
  },
  {
    id: 'hotjar',
    label: 'Hotjar',
    provider: 'Hotjar',
    category: 'analytics',
    scripts: [/static\.hotjar\.com\//i, /script\.hotjar\.com\//i],
    iframes: [],
    cookies: [
      { name: /^_hjSessionUser_/, display: '_hjSessionUser_*', duration: years(1), firstParty: true },
      { name: /^_hjSession_/, display: '_hjSession_*', duration: minutes(30), firstParty: true },
    ],
  },
  {
    id: 'clarity',
    label: 'Microsoft Clarity',
    provider: 'Microsoft',
    category: 'analytics',
    scripts: [/clarity\.ms\//i],
    iframes: [],
    cookies: [
      { name: '_clck', display: '_clck', duration: years(1), firstParty: true },
      { name: '_clsk', display: '_clsk', duration: days(1), firstParty: true },
    ],
    onAccept: () => call('clarity', 'consent', true),
    onReject: () => call('clarity', 'consent', false),
  },
  {
    id: 'linkedinInsight',
    label: 'LinkedIn Insight Tag',
    provider: 'LinkedIn',
    category: 'marketing',
    scripts: [/snap\.licdn\.com\//i, /px\.ads\.linkedin\.com\//i],
    iframes: [],
    cookies: [{ name: 'li_sugr', display: 'li_sugr', duration: months(3), firstParty: true }],
  },
  {
    id: 'tiktokPixel',
    label: 'TikTok Pixel',
    provider: 'TikTok',
    category: 'marketing',
    scripts: [/analytics\.tiktok\.com\//i],
    iframes: [],
    cookies: [{ name: '_ttp', display: '_ttp', duration: months(13), firstParty: true }],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    provider: 'Google',
    category: 'marketing',
    scripts: [/youtube\.com\/iframe_api/i, /youtube\.com\/s\/player/i],
    iframes: [/youtube(?:-nocookie)?\.com\/embed\//i],
    cookies: [
      { name: 'VISITOR_INFO1_LIVE', display: 'VISITOR_INFO1_LIVE', duration: months(6), firstParty: false },
      { name: 'YSC', display: 'YSC', duration: null, firstParty: false },
    ],
  },
  {
    id: 'vimeo',
    label: 'Vimeo',
    provider: 'Vimeo',
    category: 'functional',
    scripts: [/player\.vimeo\.com\/api/i],
    iframes: [/player\.vimeo\.com\/video\//i],
    cookies: [{ name: 'vuid', display: 'vuid', duration: years(2), firstParty: false }],
  },
  {
    id: 'googleMaps',
    label: 'Google Maps',
    provider: 'Google',
    category: 'functional',
    scripts: [/maps\.googleapis\.com\/maps\/api\/js/i],
    iframes: [/google\.[a-z.]+\/maps\/embed/i, /maps\.google\.[a-z.]+\//i],
    cookies: [{ name: 'NID', display: 'NID', duration: months(6), firstParty: false }],
  },
];

const BY_ID = new Map<TrackerId, Tracker>(TRACKERS.map((t) => [t.id, t]));

export function trackerById(id: TrackerId): Tracker {
  const tracker = BY_ID.get(id);
  if (!tracker) throw new Error(`Unknown tracker ${id}`);
  return tracker;
}

export function isTrackerId(value: string): value is TrackerId {
  return BY_ID.has(value as TrackerId);
}

/** First tracker whose script pattern matches the URL. More specific patterns are listed first per tracker. */
export function matchScript(url: string): Tracker | null {
  if (!url) return null;
  for (const t of TRACKERS) if (t.scripts.some((re) => re.test(url))) return t;
  return null;
}

export function matchIframe(url: string): Tracker | null {
  if (!url) return null;
  for (const t of TRACKERS) if (t.iframes.some((re) => re.test(url))) return t;
  return null;
}

/** Tracker that owns a cookie name, if any. */
export function matchCookie(name: string): { tracker: Tracker; cookie: TrackerCookie } | null {
  for (const tracker of TRACKERS) {
    for (const cookie of tracker.cookies) {
      if (typeof cookie.name === 'string' ? cookie.name === name : cookie.name.test(name)) return { tracker, cookie };
    }
  }
  return null;
}
